import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatFull } from "@/lib/format";
import { PROPERTY_TYPES, OPP_STATUS, CONFIDENTIALITY, CONFIDENTIALITY_LABELS, CURRENCIES } from "@/lib/constants";
import CountryCombobox from "@/components/CountryCombobox";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import PhotoManager, { itemsFromPaths } from "@/components/PhotoManager";

const EMPTY = {
  title: "", country: "France", city: "", property_type: "Villa",
  asking_price: "", renovation_cost: "", resale_value: "", surface: "",
  currency: "EUR", status: "Active", confidentiality: "Standard", description: "",
};

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Picker({ value, onChange, options, testid, labels }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>
            {labels ? labels[o] || o : o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function OpportunityForm({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [photoItems, setPhotoItems] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setPhotoItems(itemsFromPaths(initial?.photos || []));
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const asking = Number(form.asking_price) || 0;
  const reno = Number(form.renovation_cost) || 0;
  const resale = Number(form.resale_value) || 0;
  const hasResale = form.resale_value !== "" && form.resale_value !== null && form.resale_value !== undefined && !isNaN(Number(form.resale_value)) && Number(form.resale_value) > 0;
  const margin = resale - asking - reno;
  const marginPct = asking + reno > 0 ? Math.round((margin / (asking + reno)) * 1000) / 10 : 0;

  const submit = async () => {
    if (!form.title || !form.city) {
      toast.error("Titre et ville sont obligatoires");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title, country: form.country, city: form.city,
      property_type: form.property_type, asking_price: asking, renovation_cost: reno,
      resale_value: resale, surface: Number(form.surface) || 0, currency: form.currency,
      status: form.status, confidentiality: form.confidentiality, description: form.description || "",
    };
    try {
      let oppId = initial?.id;
      if (oppId) {
        await api.put(`/opportunities/${oppId}`, payload);
      } else {
        const res = await api.post("/opportunities", payload);
        oppId = res.data.id;
      }
      // Persist photos: upload new files, keep existing paths, in chosen order (index 0 = main).
      const orderedPaths = [];
      for (const item of photoItems) {
        if (item.file) {
          const fd = new FormData();
          fd.append("file", item.file);
          const up = await api.post(`/opportunities/${oppId}/photos/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          orderedPaths.push(up.data.path);
        } else if (item.path) {
          orderedPaths.push(item.path);
        }
      }
      await api.put(`/opportunities/${oppId}/photos`, { photos: orderedPaths });
      toast.success(initial?.id ? "Opportunité modifiée" : "Opportunité ajoutée");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          data-testid="opportunity-form-backdrop"
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto"
          data-testid="opportunity-form"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0F172A]">
            {initial?.id ? "Modifier l'opportunité" : "Ajouter une opportunité"}
          </DialogTitle>
          <DialogDescription className="sr-only">Formulaire de saisie des informations de l'opportunité immobilière.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Titre du projet">
              <Input data-testid="opp-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
          </div>
          <Field label="Pays">
            <CountryCombobox value={form.country} onChange={(v) => set("country", v)} testid="opp-country" />
          </Field>
          <Field label="Ville">
            <Input data-testid="opp-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label="Type de bien">
            <Picker value={form.property_type} onChange={(v) => set("property_type", v)} options={PROPERTY_TYPES} testid="opp-type" />
          </Field>
          <Field label="Devise">
            <Picker value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES} testid="opp-currency" />
          </Field>
          <Field label="Prix demandé">
            <Input type="number" data-testid="opp-price" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} />
          </Field>
          <Field label="Coût des travaux">
            <Input type="number" data-testid="opp-reno" value={form.renovation_cost} onChange={(e) => set("renovation_cost", e.target.value)} />
          </Field>
          <Field label="Valeur de revente">
            <Input type="number" data-testid="opp-resale" value={form.resale_value} onChange={(e) => set("resale_value", e.target.value)} />
          </Field>
          <Field label="Surface (m²)">
            <Input type="number" data-testid="opp-surface" value={form.surface} onChange={(e) => set("surface", e.target.value)} />
          </Field>
          <Field label="Statut">
            <Picker value={form.status} onChange={(v) => set("status", v)} options={OPP_STATUS} testid="opp-status" />
          </Field>
          <Field label="Confidentialité">
            <Picker value={form.confidentiality} onChange={(v) => set("confidentiality", v)} options={CONFIDENTIALITY} testid="opp-conf" labels={CONFIDENTIALITY_LABELS} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description">
              <Textarea data-testid="opp-description" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="mt-2">
          <Label className="text-slate-700">Photos</Label>
          <p className="mb-2 mt-0.5 text-xs text-slate-400">
            La première image (badge « Principale ») est utilisée sur la fiche. Ajoutez une image principale et au moins deux images secondaires.
          </p>
          <PhotoManager items={photoItems} onChange={setPhotoItems} />
        </div>

        <div className="mt-2 flex items-center justify-between rounded-xl bg-[#0F172A] px-5 py-4 text-white">
          <span className="overline text-[#C6A87C]">Marge calculée</span>
          {hasResale ? (
            <span className="font-display text-2xl" data-testid="opp-margin-preview">
              {formatFull(margin, form.currency)} · {marginPct}%
            </span>
          ) : (
            <span className="text-right text-sm text-slate-300" data-testid="opp-margin-preview">
              Marge indisponible – Renseignez la valeur de revente
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="opp-cancel">
            Annuler
          </Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="opp-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
